import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';
import { parseLaravelPaginator } from '../../lib/parseLaravelPaginator';
import { unwrapMaybeWrapped } from '../../lib/unwrapResource';

/**
 * Real-backend Support Tickets surface (v1.14.0) — investor self-service
 * plus a shared admin queue (manage-support; no per-ticket assignment).
 * Supersedes the old mock-backed slice entirely (that one pointed at
 * /me/tickets and /admin/tickets with a flat {message} body — neither
 * route nor shape exists on this backend).
 *
 * Same unwrapped convention as Investments — every GET/mutation returns
 * the resource or paginator directly, no `{status,data:{...}}` wrapper
 * like KYC uses. But "the docs say unwrapped" was also true of Investments
 * right up until a live single-resource GET came back as `{ data: {...} }`
 * anyway (see adminInvestmentsApi.js/investorInvestmentsApi.js) — so every
 * single-resource response here is defensively run through
 * unwrapMaybeWrapped rather than trusted at face value, same as that fix.
 *
 * Status flow: open -> resolved -> closed (terminal), with resolved -> open
 * as a side effect of any reply (investor or admin) or an explicit
 * investor reopen. closed is a dead end for everyone — messages and
 * reopen both 409 against it.
 */

function normalizeParticipant(raw) {
  if (!raw) return null;
  return {
    id: raw.uuid ?? raw.id,
    fullName: [raw.first_name, raw.last_name].filter(Boolean).join(' ') || raw.full_name || raw.email || 'Unknown',
    email: raw.email ?? null,
    roles: raw.roles ?? raw.role ?? [],
  };
}

function normalizeAttachment(raw) {
  if (!raw) return raw;
  return {
    id: raw.uuid ?? raw.id,
    originalName: raw.original_name ?? raw.filename ?? 'attachment',
    mimeType: raw.mime_type ?? null,
    size: Number(raw.size ?? 0),
    downloadUrl: raw.download_url ?? raw.url ?? null,
    createdAt: raw.created_at ?? null,
  };
}

function normalizeMessage(raw) {
  if (!raw) return raw;
  return {
    id: raw.uuid ?? raw.id,
    body: raw.body ?? raw.message ?? '',
    author: normalizeParticipant(raw.author ?? raw.user),
    attachments: (raw.attachments ?? []).map(normalizeAttachment),
    createdAt: raw.created_at ?? null,
  };
}

export function normalizeTicket(raw) {
  if (!raw) return raw;
  return {
    id: raw.uuid ?? raw.id,
    subject: raw.subject ?? '',
    category: raw.category ?? null,
    status: raw.status ?? 'open',
    investor: normalizeParticipant(raw.investor ?? raw.user),
    resolvedAt: raw.resolved_at ?? null,
    resolver: normalizeParticipant(raw.resolver),
    closedAt: raw.closed_at ?? null,
    closer: normalizeParticipant(raw.closer),
    messages: (raw.messages ?? []).map(normalizeMessage),
    createdAt: raw.created_at ?? null,
    updatedAt: raw.updated_at ?? null,
  };
}

// Multiple files under one field name need the `[]` suffix for PHP/Laravel
// to collect them into an array — a bare repeated `attachments` key would
// only keep the last file server-side.
function appendAttachments(formData, attachments) {
  (attachments ?? []).forEach((file) => formData.append('attachments[]', file));
}

// POST /investor/support-tickets — multipart, first message included.
export function buildNewTicketFormData({ subject, category, body, attachments }) {
  const formData = new FormData();
  formData.append('subject', subject);
  formData.append('category', category);
  formData.append('body', body);
  appendAttachments(formData, attachments);
  return formData;
}

// POST .../messages — multipart reply, either side.
export function buildTicketReplyFormData({ body, attachments }) {
  const formData = new FormData();
  formData.append('body', body);
  appendAttachments(formData, attachments);
  return formData;
}

export const ticketsApi = createApi({
  reducerPath: 'ticketsApi',
  baseQuery: authBaseQuery(),
  tagTypes: ['MyTicket', 'AdminTicket'],
  endpoints: (builder) => ({
    // GET /investor/support-tickets?status=&per_page=&page=
    listMyTickets: builder.query({
      query: ({ status, page, per_page } = {}) => ({
        url: '/investor/support-tickets',
        method: 'GET',
        params: { status, page, per_page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'ticketsApi:listMine' });
        return { tickets: items.map(normalizeTicket), meta, parseError: !!parseError };
      },
      providesTags: (result) => [
        ...(result?.tickets ?? []).map(({ id }) => ({ type: 'MyTicket', id })),
        { type: 'MyTicket', id: 'LIST' },
      ],
    }),

    // POST /investor/support-tickets — multipart, 201.
    createTicket: builder.mutation({
      query: (formData) => ({ url: '/investor/support-tickets', method: 'POST', data: formData }),
      transformResponse: (response) => normalizeTicket(unwrapMaybeWrapped(response)),
      invalidatesTags: [{ type: 'MyTicket', id: 'LIST' }],
    }),

    // GET /investor/support-tickets/{uuid} — own only, 404 (not 403) if
    // it belongs to someone else. No distinction attempted between "not
    // found" and "not yours" — same treatment as the Investments detail
    // screens.
    getMyTicket: builder.query({
      query: (id) => ({ url: `/investor/support-tickets/${id}`, method: 'GET' }),
      transformResponse: (response) => normalizeTicket(unwrapMaybeWrapped(response)),
      providesTags: (result, error, id) => [{ type: 'MyTicket', id }],
    }),

    // POST /investor/support-tickets/{uuid}/messages — multipart reply.
    // 409 if closed. A reply while resolved silently reopens the ticket as
    // part of this same request — the caller compares the ticket's status
    // just before submitting against the refetched status after to decide
    // whether to surface a "ticket reopened" toast, rather than trusting
    // any particular shape back from this mutation.
    replyToMyTicket: builder.mutation({
      query: ({ id, formData }) => ({ url: `/investor/support-tickets/${id}/messages`, method: 'POST', data: formData }),
      invalidatesTags: (result, error, { id }) => [{ type: 'MyTicket', id }],
    }),

    // POST /investor/support-tickets/{uuid}/reopen — no body. 409 if
    // closed, no-op 200 if already open.
    reopenMyTicket: builder.mutation({
      query: (id) => ({ url: `/investor/support-tickets/${id}/reopen`, method: 'POST' }),
      invalidatesTags: (result, error, id) => [{ type: 'MyTicket', id }],
    }),

    // GET /admin/support-tickets?status=&category=&per_page=&page= —
    // permission: manage-support. Shared queue, no assignment concept.
    listAdminTickets: builder.query({
      query: ({ status, category, page, per_page } = {}) => ({
        url: '/admin/support-tickets',
        method: 'GET',
        params: { status, category, page, per_page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'ticketsApi:listAdmin' });
        return { tickets: items.map(normalizeTicket), meta, parseError: !!parseError };
      },
      providesTags: (result) => [
        ...(result?.tickets ?? []).map(({ id }) => ({ type: 'AdminTicket', id })),
        { type: 'AdminTicket', id: 'LIST' },
      ],
    }),

    // GET /admin/support-tickets/{uuid} — any investor's ticket.
    getAdminTicket: builder.query({
      query: (id) => ({ url: `/admin/support-tickets/${id}`, method: 'GET' }),
      transformResponse: (response) => normalizeTicket(unwrapMaybeWrapped(response)),
      providesTags: (result, error, id) => [{ type: 'AdminTicket', id }],
    }),

    // POST /admin/support-tickets/{uuid}/messages — multipart admin reply.
    // Same 409-if-closed / auto-reopen-if-resolved behavior as the investor
    // reply route.
    replyToAdminTicket: builder.mutation({
      query: ({ id, formData }) => ({ url: `/admin/support-tickets/${id}/messages`, method: 'POST', data: formData }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AdminTicket', id },
        { type: 'AdminTicket', id: 'LIST' },
      ],
    }),

    // POST /admin/support-tickets/{uuid}/resolve — no body. 409 if already
    // closed. Emails the investor (server-side, nothing to do here).
    resolveTicket: builder.mutation({
      query: (id) => ({ url: `/admin/support-tickets/${id}/resolve`, method: 'POST' }),
      transformResponse: (response) => normalizeTicket(unwrapMaybeWrapped(response)),
      invalidatesTags: (result, error, id) => [
        { type: 'AdminTicket', id },
        { type: 'AdminTicket', id: 'LIST' },
      ],
    }),

    // POST /admin/support-tickets/{uuid}/close — terminal, no email. Once
    // closed the ticket is fully locked for everyone.
    closeTicket: builder.mutation({
      query: (id) => ({ url: `/admin/support-tickets/${id}/close`, method: 'POST' }),
      transformResponse: (response) => normalizeTicket(unwrapMaybeWrapped(response)),
      invalidatesTags: (result, error, id) => [
        { type: 'AdminTicket', id },
        { type: 'AdminTicket', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListMyTicketsQuery,
  useCreateTicketMutation,
  useGetMyTicketQuery,
  useReplyToMyTicketMutation,
  useReopenMyTicketMutation,
  useListAdminTicketsQuery,
  useGetAdminTicketQuery,
  useReplyToAdminTicketMutation,
  useResolveTicketMutation,
  useCloseTicketMutation,
} = ticketsApi;
